import { WbsListDto, WbsPlanningDto } from '@api/client';
import { formatCurrency } from 'shared/utils/common.utils';
import { Card, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './styles.scss';

interface Props {
  deliverablesTableDataShown: WbsListDto[] | undefined;
  totalPercentagePerMonth: number[];
  totalCostPerMonth: number[];
  totalTotSalPercentage: number;
  totalTotSalCost: number;
  isWorkInProgress: boolean;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  /** Ref sul container scrollabile "mesi" di questa tabella, usato da useSynchronizedScroll (istanziato in wbs/index.tsx) per sincronizzare lo scroll orizzontale con la tabella WBS principale. */
  totalsScrollRef?: React.RefObject<HTMLDivElement>;
  /** Notifica lo scroll orizzontale di questo container, con lo scrollLeft corrente, cosi' la tabella WBS principale puo' seguirlo. */
  onChildScroll?: (scrollLeft: number) => void;
}

const WbsTotalsTable: React.FC<Props> = ({
  deliverablesTableDataShown,
  totalPercentagePerMonth,
  totalCostPerMonth,
  totalTotSalPercentage,
  totalTotSalCost,
  isWorkInProgress,
  isCollapsed,
  toggleCollapse,
  totalsScrollRef,
  onChildScroll,
}) => {
  const { t } = useTranslation();

  const monthColumns = deliverablesTableDataShown?.[0]?.wbsPlanningDtoList;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    onChildScroll?.(target.scrollLeft);
  };

  return (
    <Card className="totals-card">
      <Card.Header onClick={toggleCollapse} className="d-flex justify-content-end">
        <p className="text-blue-links m-0" style={{ cursor: 'pointer' }}>
          {isCollapsed ? `${t('project-detail.show-sums')}` : `${t('project-detail.hide-sums')}`}
        </p>
        <i
          className={`text-blue-links align-content-center fa-solid fa-chevron-${isCollapsed ? 'up' : 'down'} ms-2`}
        />
      </Card.Header>
      <Card.Body className="pt-0">
        <div
          className="d-flex table-responsive table-bordered"
          style={{ height: isCollapsed ? '0' : '100%', overflow: 'auto' }}
          ref={totalsScrollRef}
          onScroll={handleScroll}
        >
          <Table className="revenue-table sticky-table mt-0 mb-0">
            <tbody className="piede-tab">
              <tr key="wbs-totals-label-percentage">
                <td className="text-left p-0" style={{ minWidth: '590px', maxWidth: '590px' }}>
                  {t('Total (%)')}
                </td>
                {isWorkInProgress && (
                  <>
                    <td className="text-left p-0 bg-disabled" style={{ minWidth: '150px', maxWidth: '150px' }} />
                    <td className="text-left p-0 bg-disabled" style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <span className="span-table-values">{formatCurrency(totalTotSalPercentage)}%</span>
                    </td>
                  </>
                )}
              </tr>
              <tr key="wbs-totals-label-currency">
                <td className="text-left p-0" style={{ minWidth: '590px', maxWidth: '590px' }}>
                  {t('Total (€)')}
                </td>
                {isWorkInProgress && (
                  <>
                    <td className="text-left p-0 bg-disabled" style={{ minWidth: '150px', maxWidth: '150px' }} />
                    <td className="text-left p-0 bg-disabled" style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <span className="span-table-values">{formatCurrency(totalTotSalCost)}€</span>
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </Table>
          <Table className="revenue-table mt-0 mb-0">
            <tbody>
              <tr key="wbs-totals-row-percentage">
                {monthColumns?.map((allocation: WbsPlanningDto, index: number) => (
                  <td
                    className={`text-center p-0 ${allocation?.enable === false && 'bg-disabled'}`}
                    key={'wbs-total-percentage-' + index}
                    style={{ minWidth: '100px', maxWidth: '100px' }}
                  >
                    <span className="span-table-values">
                      {formatCurrency(totalPercentagePerMonth[index])}%
                    </span>
                  </td>
                ))}
              </tr>
              <tr key="wbs-totals-row-currency">
                {monthColumns?.map((allocation: WbsPlanningDto, index: number) => (
                  <td
                    className={`text-center p-0 ${allocation?.enable === false && 'bg-disabled'}`}
                    key={'wbs-total-currency-' + index}
                    style={{ minWidth: '100px', maxWidth: '100px' }}
                  >
                    <span className="span-table-values">
                      {formatCurrency(totalCostPerMonth[index])}€
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default WbsTotalsTable;
